export const decodeHTMLEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Multi-character UTF-8 mojibake patterns. These are the only encoding
// fixes we apply unconditionally on the client.
//
// We previously also mapped single Latin-1 characters like £ (U+00A3),
// ³ (U+00B3), ¶ (U+00B6), ¿ (U+00BF), ñ (U+00F1), æ (U+00E6), Å (U+00C5)
// to Polish letters, on the theory that a feed declaring ISO-8859-2 might
// have its bytes mis-decoded as Latin-1. In practice the server already
// decodes by Content-Type + XML declaration, so those single-char fixes
// never fixed real bugs — they only corrupted legitimate Unicode (BBC's
// "£500" → "Ł500", "m³" → "mł", Spanish "¿Cómo" → "żCómo", Swedish
// "Måneskin" → "MŁneskin"). They've been removed.
//
// The patterns below are safe because each is a multi-byte sequence
// extremely unlikely to occur naturally — they're the canonical
// signatures of UTF-8 misread as Latin-1.
const polishCharFixes: Record<string, string> = {
  'Ã³': 'ó',
  'Ã"': 'Ó',
  'Ä…': 'ą',
  'Ä„': 'Ą',
  'Ä‡': 'ć',
  'Ä†': 'Ć',
  'Ä™': 'ę',
  'Ä˜': 'Ę',
  'Å‚': 'ł',
  'Å„': 'ń',
  'Åƒ': 'Ń',
  'Å›': 'ś',
  'ÅŠ': 'Ś',
  'Å¼': 'ż',
  'Å»': 'Ż',
  'Åº': 'ź',
  'Å¹': 'Ź',

  // Double-encoding artefacts
  'Ã‚Â³': 'ł',
  'Ã‚Â¹': 'ą',
};

export const fixEncoding = (text: string): string => {
  if (!text) return text;

  try {
    let result = text;

    // Apply multi-character mojibake replacements. Single-char Latin-1
    // mappings (£, ³, ñ, etc.) used to live here too but corrupted valid
    // Unicode in non-Polish content — see the comment on polishCharFixes.
    for (const [bad, good] of Object.entries(polishCharFixes)) {
      result = result.split(bad).join(good);
    }

    // If the text still contains U+FFFD replacement chars after the above,
    // it's already lossy — re-decoding the JS string as bytes won't add
    // information back. The old fallback paths that did this were prone
    // to silently making things worse, so they've been dropped.
    return result;
  } catch {
    return text;
  }
};

// Additional function to fix specific Polish RSS feed issues
export const fixPolishText = (text: string): string => {
  if (!text) return text;

  let result = text;

  // Fix common HTML entities for Polish characters
  const htmlEntities: Record<string, string> = {
    '&oacute;': 'ó',
    '&Oacute;': 'Ó',
    '&aogon;': 'ą',
    '&Aogon;': 'Ą',
    '&cacute;': 'ć',
    '&Cacute;': 'Ć',
    '&eogon;': 'ę',
    '&Eogon;': 'Ę',
    '&lstrok;': 'ł',
    '&Lstrok;': 'Ł',
    '&nacute;': 'ń',
    '&Nacute;': 'Ń',
    '&sacute;': 'ś',
    '&Sacute;': 'Ś',
    '&zacute;': 'ź',
    '&Zacute;': 'Ź',
    '&zdot;': 'ż',
    '&Zdot;': 'Ż',
    // Numeric entities
    '&#243;': 'ó',
    '&#211;': 'Ó',
    '&#261;': 'ą',
    '&#260;': 'Ą',
    '&#263;': 'ć',
    '&#262;': 'Ć',
    '&#281;': 'ę',
    '&#280;': 'Ę',
    '&#322;': 'ł',
    '&#321;': 'Ł',
    '&#324;': 'ń',
    '&#323;': 'Ń',
    '&#347;': 'ś',
    '&#346;': 'Ś',
    '&#378;': 'ź',
    '&#377;': 'Ź',
    '&#380;': 'ż',
    '&#379;': 'Ż',
  };

  for (const [entity, char] of Object.entries(htmlEntities)) {
    result = result.split(entity).join(char);
  }

  return result;
};

export const decodeBase64DataUrl = (dataUrl: string): string => {
  // Handle data URLs like: data:application/rss+xml;charset=utf-8;base64,PHJzcy...
  const base64Match = dataUrl.match(/^data:[^;]+;[^;]*;base64,(.+)$/);
  if (base64Match) {
    try {
      // Decode base64 to binary string
      const binaryString = atob(base64Match[1]);
      // Convert to Uint8Array
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      // Decode as UTF-8
      const decoder = new TextDecoder('utf-8', { fatal: false });
      return decoder.decode(bytes);
    } catch {
      return atob(base64Match[1]);
    }
  }
  // Also handle simpler format: data:...;base64,
  const simpleMatch = dataUrl.match(/;base64,(.+)$/);
  if (simpleMatch) {
    try {
      const binaryString = atob(simpleMatch[1]);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const decoder = new TextDecoder('utf-8', { fatal: false });
      return decoder.decode(bytes);
    } catch {
      return atob(simpleMatch[1]);
    }
  }
  return dataUrl;
};
